


























import Vue from 'vue';
import { mapGetters } from 'vuex';
import CommunityCommentNode from '@/web/components/community/CommunityCommentNode';
import { getStudioURL } from '@/web/router/custom-domain-utils';
import { errorToMessage } from '@/core/utils/errors';
import * as types from '@/core/store/mutation-types';

export default Vue.extend({
  name: 'CommunityCommentsSearch',
  components: {
    CommunityCommentNode,
  },
  props: {
    entityOwnerId: {
      type: String,
    },
    entityType: {
      type: String,
    },
    entityId: {
      type: Number,
    },
    additionalData: {
      type: Object,
    },
    query: {
      type: String,
    },
    hashtagsEnabled: {
      type: Boolean,
    },
    hashtags: {
      type: Array,
    },
  },
  computed: {
    ...mapGetters({
      interactAsStudio: 'community/interactAsStudio',
    }),
    studioURL() {
      return getStudioURL(this.$route);
    },
    hasComments() {
      return this.comments?.length > 0;
    },
  },
  data() {
    return {
      comments: [],
      isLoading: true,
      currentUser: {},
      page: 1,
      hasMoreComments: false,
    };
  },
  methods: {
    async loadData() {
      try {
        this.page = 1;
        this.isLoading = true;
        this.comments = await this.$store.dispatch('community/searchComments', {
          entityType: this.entityType,
          entityId: this.entityId,
          entityOwnerId: this.entityOwnerId,
          query: this.query,
          page: this.page,
        });
        this.hasMoreComments = this.comments?.length > 0;
        const commentIdParam = this.$route.query.comment;
        if (commentIdParam) {
          this.$nextTick(() => {
            const commentElement = document.getElementById(
              `comment-${commentIdParam}`
            );
            if (commentElement) {
              const yOffset = -60;
              const y =
                commentElement.getBoundingClientRect().top +
                window.pageYOffset +
                yOffset;
              window.scrollTo({ top: y });
            }
          });
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      } finally {
        this.isLoading = false;
      }
    },
    async loadMoreComments() {
      if (!this.hasMoreComments) return;
      try {
        this.page += 1;
        this.isLoading = true;
        const numberOfComments = this.comments.length;
        this.comments = await this.$store.dispatch('community/searchComments', {
          entityType: this.entityType,
          entityId: this.entityId,
          entityOwnerId: this.entityOwnerId,
          query: this.query,
          page: this.page,
        });
        this.hasMoreComments = this.comments?.length > numberOfComments;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      } finally {
        this.isLoading = false;
      }
    },
    async loadUserData() {
      this.isLoading = true;
      try {
        this.currentUser = await this.$store.dispatch(
          'community/loadMyCommunityProfileData'
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(
            error.response,
            `${this.$t('COMMUNITY.TEXT.PROFILE_LOADING_ERROR')}: ${error}`
          ),
          duration: -1,
        });
      } finally {
        this.isLoading = false;
      }
    },
    async postComment(data) {
      const parentId = data.parentId;
      const entityType = this.entityType;
      const entityId = this.entityId;
      const entityOwnerId = this.entityOwnerId;
      const content = data.content;
      const text = data.text;
      const isStudio = this.interactAsStudio;
      const additionalData = this.additionalData;
      const attachment = data?.attachment;
      const payload = {
        parentId,
        entityType,
        entityId,
        entityOwnerId,
        content,
        text,
        additionalData,
        isStudio,
        attachment,
      };
      const comment = await this.$store.dispatch(
        'community/postComment',
        payload
      );
      return comment;
    },
    async editComment(data) {
      const commentId = data.id;
      const content = data.content;
      const text = data.text;
      const isStudio = this.interactAsStudio;
      const additionalData = this.additionalData;
      const attachment = data?.attachment;
      const payload = {
        commentId,
        content,
        text,
        additionalData,
        isStudio,
        attachment,
      };
      const comment = await this.$store.dispatch(
        'community/editComment',
        payload
      );
      return comment;
    },
    async addReply(data) {
      const payload = {
        parentId: data.parentId,
        content: data.json,
        text: data.text,
        attachment: data.attachment,
      };
      try {
        const comment = await this.postComment(payload);
        const parentComment = this.findComment(this.comments, payload.parentId);
        if (parentComment) {
          parentComment.replies.push(comment);
        }
        if (data.successFn) data.successFn();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response, `Post comment: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    findComment(commentsList, id) {
      for (let i = 0; i < commentsList.length; i++) {
        if (commentsList[i].id === id) {
          return commentsList[i];
        }
        const found = this.findComment(commentsList[i].replies, id);
        if (found) return found;
      }
      return null;
    },
    async editReply(data) {
      const payload = {
        id: data.id,
        parentId: data.parentId,
        content: data.json,
        text: data.text,
        attachment: data.attachment,
      };
      try {
        const comment = await this.editComment(payload);
        const editedComment = this.findComment(this.comments, comment.id);
        if (editedComment) {
          editedComment.content = comment.content;
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response, `Post comment: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    onHashtag(hashtag) {
      this.$emit('onHashtag', hashtag);
    },
  },
  async mounted() {
    await this.loadUserData();
    await this.loadData();
  },
  watch: {
    async interactAsStudio(value) {
      await this.loadUserData();
      await this.loadData();
    },
    async query(value) {
      await this.loadData();
    },
  },
});
